import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, PawPrint, Shield, HandHeart, ArrowRight, Tent, Sparkle } from "@phosphor-icons/react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/1dceb75f75b3340c8ae483c30ff137873e1faa66c158c79722ab9618963c52a2.jpeg";
const CAMP = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/140fb40bd74e3179046146cbf21105788ad2c2df6bbe0296c877564efc7eb11e.jpeg";
const PLEDGE = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/6b6ccacba132aec90c1f2c87a7b01221f5108f7ba2210c415f860fed8ea0e608.jpeg";
const RESCUE = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/de7084fed1059ba5ae5d4a2f938f538d865c082169863d8fbf34c34af8b97d0f.jpeg";

const PILLARS = [
  { icon: Shield, title: "Protect the kids", body: "A portion of every drop funds safe spaces, meals and mentorship for kids who've been dealt a rough hand." },
  { icon: PawPrint, title: "Rescue the dogs", body: "The same pits the world writes off become the loyal protectors these kids never had. We heal both ends of the leash." },
  { icon: HandHeart, title: "Build them a home", body: "The Heaven ranch is for them too — cabins, open land, and a pack that shows up. Nobody left behind." },
];

const CAMPS = [
  { icon: Tent, title: "The Summer Pack Camp", body: "Two weeks on the ranch. Kids and rescued pits learn trust, structure and love — side by side. Fully funded by the drops.", tag: "300 acres" },
  { icon: HandHeart, title: "Healing Kennels", body: "Trauma-trained programs where a scared kid rehabs a scarred pit. Two broken things becoming whole, together.", tag: "Both ends of the leash" },
  { icon: Sparkle, title: "The Mentor Pack", body: "Every camp grad gets a mentor and a pit for life. Not a program that ends — a family that doesn't quit.", tag: "For life" },
];

const STATS = [
  { n: "$3M", l: "Mission goal" },
  { n: "300", l: "Acres of Heaven" },
  { n: "3,000", l: "Pits off the list" },
  { n: "∞", l: "Kids we won't quit on" },
];

export default function SaveTheChildren() {
  return (
    <div data-testid="save-the-children-page" className="bg-background">
      {/* Hero */}
      <section className="relative h-[74vh] min-h-[480px] overflow-hidden">
        <img src={HERO} alt="A child and a rescued pit bull" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/45 to-background/10" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-7xl mx-auto">
          <p className="overline text-primary mb-3 inline-flex items-center gap-2"><Heart weight="fill" size={14} /> The Mission · Save the Children</p>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-3xl">
            Your fit stands<br /><span className="neon-text">with pits.</span>
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl text-base sm:text-lg">
            The broken kids and the throwaway dogs need the exact same thing — somebody who refuses to give up on them. Every piece you wear says out loud: I'm that somebody.
          </p>
        </div>
      </section>

      {/* Belief band */}
      <section className="border-y border-white/10 bg-black">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 text-center">
          <p className="font-display font-black uppercase text-2xl sm:text-4xl leading-tight">
            Save a kid. <span style={{ color: "#ccff00" }}>Save a pit.</span> Same fight.
          </p>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">Nobody chose the corner they were born into. We don't judge the underdog — we adopt them, both the two-legged and the four. That's the whole brand.</p>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-7 rounded-2xl bg-card border border-primary/20 neon-glow" data-testid={`stc-pillar-${i}`}>
              <p.icon weight="fill" size={26} className="text-primary mb-4" />
              <h3 className="font-modern text-xl mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* The Camps */}
      <section className="relative overflow-hidden py-20" data-testid="stc-camps">
        <img src={CAMP} alt="The Wigglez Camps" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-background/80" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <p className="overline mb-3" style={{ color: "#ccff00" }}>The Wigglez Camps</p>
          <h2 className="font-display font-black uppercase text-3xl sm:text-5xl mb-3 max-w-2xl">Where two lives become one lifeline.</h2>
          <p className="text-muted-foreground max-w-2xl mb-10">On the ranch, a scared kid and a scarred pit find each other and decide — together — that they're worth saving. Here's how we make that happen a thousand more times.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {CAMPS.map((c, i) => (
              <motion.div key={c.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-7 rounded-2xl bg-card/90 backdrop-blur border border-secondary/25 neon-glow-aqua" data-testid={`stc-camp-${i}`}>
                <c.icon weight="fill" size={26} className="text-secondary mb-4" />
                <p className="overline text-secondary mb-2">{c.tag}</p>
                <h3 className="font-modern text-xl mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.l} className="text-center rounded-2xl border border-white/10 py-8 bg-card" data-testid={`stc-stat-${s.l}`}>
              <p className="font-display font-black text-4xl sm:text-5xl neon-text">{s.n}</p>
              <p className="overline text-muted-foreground mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Story split */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid md:grid-cols-2 gap-10 items-center">
        <div className="rounded-2xl overflow-hidden border border-primary/20 neon-glow">
          <img src={RESCUE} alt="A rescued pit bull" className="w-full h-full object-cover" />
        </div>
        <div>
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl mb-5">Two lives, one lifeline.</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We've watched a scared kid and a scarred pit find each other and decide, together, that they're worth saving. That's not a coincidence. That's the whole point.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Every hoodie, every wrap, every dollar builds the safe place where that moment can happen a thousand more times.
          </p>
        </div>
      </section>

      {/* Pledge / Bring Them Home CTA */}
      <section className="relative overflow-hidden py-24 mt-6" data-testid="stc-pledge">
        <img src={PLEDGE} alt="Bring them home" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display font-black uppercase text-4xl sm:text-6xl mb-5">Be the somebody.</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">Join the movement. Every gift and every piece worn brings a kid — and a pit — one step closer to home.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/donate" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="stc-donate-cta">
              <Heart weight="fill" size={18} /> Donate to the Mission
            </Link>
            <Link to="/shop" className="inline-flex items-center gap-2 border border-secondary/60 text-secondary px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-secondary/10 transition-colors neon-glow-aqua" data-testid="stc-shop-cta">
              Shop the Movement <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
